import Navbar from "./Navbar";
import Footer from "./Footer";
import React, { useState, useEffect } from 'react';
import { useAppData } from './context/DataContext';
import axios from 'axios';
import { Button, Dropdown, Space, message, Modal, Alert, Input } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { ReactComponent as Avatar } from "./IconUmmahfy.svg";
import { ReactComponent as Ellipsis } from "./Ellipsis.svg";
import { useNavigate } from 'react-router-dom';
import FacebookLogin from "./FacebookLogin.js";
import OrganizationSelector from './OrganizationSelector';
import analyticsService from './services/analytics.service';

const secrets = require('./secrets.js');
const api = secrets.server_url;

const queryParameters = new URLSearchParams(window.location.search)
const sessionId = queryParameters.get("session_id")

const userTypeItems = [
    {
        key: 'Collaborator',
        label: 'Collaborator',
    },
    {
        key: 'Administrator',
        label: 'Administrator',
    },
    {
        key: 'Co-Owner',
        label: 'Co-Owner',
    },
    {
        key: 'Owner',
        label: 'Owner',
    },
];

function OrganizerSettings() {

    const navigate = useNavigate();

    const {
        selectedOrganization,
        selectedOrganizationInfo,
        managedOrganizations,
        setAppData
    } = useAppData();

    const analytics = analyticsService;

    const [users, setUsers] = useState(() => {
        if (!selectedOrganizationInfo) return [];

        return [
            // Owner - convert from ObjectId reference if needed
            {
                user: typeof selectedOrganizationInfo.owner === 'object'
                    ? selectedOrganizationInfo.owner
                    : { _id: selectedOrganizationInfo.owner, email: 'Loading...' },
                role: 'Owner',
                isOwner: true
            },
            // Co-owner - convert from ObjectId reference if needed
            ...(selectedOrganizationInfo.co_owner ? [{
                user: typeof selectedOrganizationInfo.co_owner === 'object'
                    ? selectedOrganizationInfo.co_owner
                    : { _id: selectedOrganizationInfo.co_owner, email: 'Loading...' },
                role: 'Co-Owner',
                isOwner: false
            }] : []),
            // Admins
            ...(selectedOrganizationInfo.admins || []).map(admin => ({
                ...admin,
                role: 'Administrator',
                isOwner: false
            })),
            // Collaborators
            ...(selectedOrganizationInfo.collaborators || []).map(collab => ({
                ...collab,
                role: 'Collaborator',
                isOwner: false
            }))
        ];
    });

    // Add useEffect to update users state when selectedOrganizationInfo changes
    useEffect(() => {
        if (!selectedOrganizationInfo) {
            setUsers([]);
            // Reset other organization-dependent state values
            setPendingChange(null);
            setConnectedAccountId(undefined);
            setNewUser('');
            setNewUserType('Collaborator');
            setError(false);
            return;
        }

        // Update users list based on new organization info
        setUsers([
            // Owner - convert from ObjectId reference if needed
            {
                user: typeof selectedOrganizationInfo.owner === 'object'
                    ? selectedOrganizationInfo.owner
                    : { _id: selectedOrganizationInfo.owner, email: 'Loading...' },
                role: 'Owner',
                isOwner: true
            },
            // Co-owner - convert from ObjectId reference if needed
            ...(selectedOrganizationInfo.co_owner ? [{
                user: typeof selectedOrganizationInfo.co_owner === 'object'
                    ? selectedOrganizationInfo.co_owner
                    : { _id: selectedOrganizationInfo.co_owner, email: 'Loading...' },
                role: 'Co-Owner',
                isOwner: false
            }] : []),
            // Admins
            ...(selectedOrganizationInfo.admins || []).map(admin => ({
                ...admin,
                role: 'Administrator',
                isOwner: false
            })),
            // Collaborators
            ...(selectedOrganizationInfo.collaborators || []).map(collab => ({
                ...collab,
                role: 'Collaborator',
                isOwner: false
            }))
        ]);

        // Update Stripe connected account id if available
        if (selectedOrganizationInfo.stripeAccountId) {
            setConnectedAccountId(selectedOrganizationInfo.stripeAccountId);
        }
    }, [selectedOrganizationInfo]);

    const [pendingChange, setPendingChange] = useState(null);
    const [accountCreatePending, setAccountCreatePending] = useState(false);
    const [accountLinkCreatePending, setAccountLinkCreatePending] = useState(false);
    const [error, setError] = useState(false);
    const [connectedAccountId, setConnectedAccountId] = useState();

    const [newUser, setNewUser] = useState('');
    const [newUserType, setNewUserType] = useState('Collaborator');

    const [newOrgName, setNewOrgName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameError, setNameError] = useState('');

    useEffect(() => {
        if (sessionId) {
            axios.get(`${api}stripe/retrieve-session/${sessionId}`)
                .then(response => {
                    console.log(response.data)
                    console.log(`Amount Paid: ${response.data.amount_total / 100}`)
                    console.log(`Payment Status: ${response.data.payment_status}`)
                    console.log(`Customer Email: ${response.data.customer_details.email}`)
                    console.log(`Transaction ID: ${response.data.id}`)
                    console.log(`Product Name: ${response.data.line_items.data[0].description}`)
                    console.log(`receipt url: ${response.data.payment_intent.charges.data[0].receipt_url}`)
                })
                .catch(error => {
                    console.error('Unable to retrieve payment details. Please contact support.');
                });
        }
    }, [sessionId]);

    // Reset edit state when organization changes
    useEffect(() => {
        if (selectedOrganizationInfo) {
            setNewOrgName(selectedOrganizationInfo.name || '');
        }
        setIsEditingName(false);
        setNameError('');
    }, [selectedOrganizationInfo]);

    const handleRoleChange = (user, newRole) => {
        setPendingChange({ user, newRole });
    };

    const confirmRoleChange = async () => {
        if (!pendingChange) return;

        const { user, newRole } = pendingChange;
        let data = {};
        const userId = user.user?._id;

        if (!userId) {
            console.error('No user ID found for role change');
            return;
        }

        // Remove user from their current role
        if (user.role === 'Co-Owner') {
            data.co_owner = null; // Clear co-owner
        } else if (user.role === 'Administrator') {
            data.admins = selectedOrganizationInfo.admins.filter(admin =>
                admin.user._id !== userId && admin.user !== userId
            );
        } else if (user.role === 'Collaborator') {
            data.collaborators = selectedOrganizationInfo.collaborators.filter(collab =>
                collab.user._id !== userId && collab.user !== userId
            );
        }

        // Add user to their new role
        if (newRole === 'Co-Owner') {
            data.co_owner = userId;
        } else if (newRole === 'Administrator') {
            data.admins = [
                ...(data.admins || selectedOrganizationInfo.admins || []),
                { user: userId, status: user.status || 'active' }
            ];
        } else if (newRole === 'Collaborator') {
            data.collaborators = [
                ...(data.collaborators || selectedOrganizationInfo.collaborators || []),
                { user: userId, status: user.status || 'active' }
            ];
        }

        try {
            await axios.put(`${api}organization/${selectedOrganizationInfo._id}`, data, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json',
                },
            });

            // Update local state with new role
            setUsers(users.map(u =>
                (u.user?._id === userId) ? { ...u, role: newRole } : u
            ));

            setPendingChange(null);
        } catch (err) {
            console.error('Error updating user role:', err);
        }
    };

    const cancelRoleChange = () => {
        setPendingChange(null);
    };

    const removeUser = async (userType, user) => {
        const userId = user.user?._id;
        if (!userId) {
            console.error('No user ID found for removal');
            return;
        }

        let data = {};
        if (userType === "Collaborator") {
            data.collaborators = selectedOrganizationInfo.collaborators.filter(
                item => item.user._id !== userId && item.user !== userId
            );
        } else if (userType === "Administrator") {
            data.admins = selectedOrganizationInfo.admins.filter(
                item => item.user._id !== userId && item.user !== userId
            );
        } else if (userType === "Co-Owner") {
            data.co_owner = null; // Clear co-owner
        }

        try {
            // Remove user from organization
            const response = await axios.put(`${api}organization/${selectedOrganizationInfo._id}`, data, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json',
                },
            });

            // Remove organization from user's list
            const userResponse = await axios.put(`${api}user/${userId}`, {
                $pull: { organizations: selectedOrganizationInfo._id }
            }, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json',
                },
            });

            // Update local state
            setUsers(users.filter(u =>
                u.user?._id !== userId
            ));
        } catch (err) {
            console.error('Error removing user:', err);
        }
    };

    const handleAddUser = async (event) => {
        event.preventDefault();

        if (!newUser || !newUser.trim()) {
            setError('Please enter a valid email address');
            return;
        }

        try {
            // First, check if the user exists by email
            const userResponse = await axios.get(`${api}users/${newUser}`, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                }
            });

            const userData = userResponse?.data?.data;
            let userId;

            if (userData) {
                // User exists, use their ID
                userId = userData._id;
            } else {
                // User doesn't exist, create a new one
                const newUserResponse = await axios.post(`${api}users/`, {
                    email: newUser,
                    name: newUser.split('@')[0]
                }, {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                        'Content-Type': 'application/json'
                    }
                });

                if (newUserResponse?.data?.data?.user) {
                    userId = newUserResponse.data.data.user._id;
                } else {
                    throw new Error('Failed to create user');
                }
            }

            // Now, add the user to the organization with their role
            let data = {};
            if (newUserType === "Collaborator") {
                data.collaborators = [
                    ...(selectedOrganizationInfo.collaborators || []),
                    { user: userId, status: "pending" }
                ];
            } else if (newUserType === "Administrator") {
                data.admins = [
                    ...(selectedOrganizationInfo.admins || []),
                    { user: userId, status: "pending" }
                ];
            } else if (newUserType === "Co-Owner") {
                // Only existing active users can be made co-owners
                if (isUserActiveInOrg(userId)) {
                    data.co_owner = userId;
                } else {
                    setError("New Co-Owner must first be an active user of the organization");
                    return;
                }
            } else if (newUserType === "Owner") {
                // Only existing active users can be made owners
                if (isUserActiveInOrg(userId)) {
                    data.owner = userId;
                } else {
                    setError("New Owner must first be an active user of the organization");
                    return;
                }
            }

            // Update the organization
            await axios.put(`${api}organization/${selectedOrganizationInfo._id}`, data, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                    'Content-Type': 'application/json'
                }
            });

            // Send invitation email
            try {
                await axios.post(`${api}inviteToOrg`, {
                    email: newUser,
                    accountType: "User",
                    requestType: "Invitation",
                    organizationId: selectedOrganizationInfo._id
                }, {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                    }
                });
            } catch (emailError) {
                console.error('Failed to send invitation email:', emailError);
            }

            // Update local state with new user
            const newUserData = {
                user: { _id: userId, email: newUser },
                role: newUserType,
                isOwner: newUserType === 'Owner',
                status: "pending"
            };

            setUsers([...users, newUserData]);
            setNewUser('');
            setNewUserType('Collaborator');
            setError('');
        } catch (err) {
            console.error('Error adding user:', err);
            setError('Failed to add user: ' + (err.response?.data?.message || err.message));
        }
    };

    // Helper function to check if user is active in organization
    const isUserActiveInOrg = (userId) => {
        const isAdmin = selectedOrganizationInfo.admins?.some(
            admin => (admin.user === userId || admin.user._id === userId) && admin.status === 'active'
        );

        const isCollaborator = selectedOrganizationInfo.collaborators?.some(
            collab => (collab.user === userId || collab.user._id === userId) && collab.status === 'active'
        );

        const isCoOwner = selectedOrganizationInfo.co_owner === userId ||
            (selectedOrganizationInfo.co_owner && selectedOrganizationInfo.co_owner._id === userId);

        return isAdmin || isCollaborator || isCoOwner;
    };

    const renderUserRow = (user) => (
        <div className={`user-row ${user.isOwner ? 'user-org-owner' : ''}`} key={user.user?._id || user.email}>
            <div className="user-prof-email">
                <Avatar className="user-prof-pic" />
                <p>{user.user?.email || user.email || 'Loading...'}</p>
                {user.status === "pending" && (
                <div className="user-status">
                    <span className="pending-status">{user.status}</span>
                </div>
            )}

            {user.status === "active" && (
                <div className="user-status">
                    <span className="active-status"> </span>
                </div>
            )}
            </div>



            <div className="user-priv-div">
                {user.isOwner ? (
                    <div className="owner-role-display">
                        {user.role}
                    </div>
                ) : (
                    <Dropdown
                        className="user-collab-manage"
                        menu={{
                            items: [
                                { key: 'Owner', label: 'Owner' },
                                { key: 'Co-Owner', label: 'Co-Owner' },
                                { key: 'Administrator', label: 'Administrator' },
                                { key: 'Collaborator', label: 'Collaborator' },
                            ],
                            onClick: ({ key }) => handleRoleChange(user, key)
                        }}
                        placement="bottom"
                        arrow
                    >
                        <Button>
                            {user.role}
                        </Button>
                    </Dropdown>
                )}

                {pendingChange && pendingChange.user.user?._id === user.user?._id && (
                    <div className="confirm-buttons">
                        <Button className="confirm-btn" onClick={confirmRoleChange}>
                            <span className="confirm"> </span>
                        </Button>

                        <Button className="cancel-btn" onClick={cancelRoleChange}>
                            <span className="cancel"> </span>
                        </Button>
                    </div>
                )}
            </div>



            {!user.isOwner && (
                <Dropdown
                    className=""
                    placement="bottomRight"
                    menu={{
                        items: [
                            {
                                key: 'Delete',
                                label: 'Delete',
                                onClick: () => removeUser(user.role, user)
                            },
                        ],
                        onClick: ({ key }) => {
                            if (key === 'Delete') {
                                removeUser(user.role, user);
                            }
                            // You can add more conditions here if you add more items
                        }
                    }}
                >
                    <Button className="user-remove-btn">
                        <Ellipsis className="trash-icon" />
                    </Button>
                </Dropdown>
            )}
        </div>
    );

    const handleMenuClick = (e) => {
        setNewUserType(e.key);
    };

    const onFacebookLoginSuccess = async (userData) => {
        console.log(userData)
        //exchange token for long-lived token

        const response = await axios.post(`${api}exchangeFacebookToken`, {
            token: userData.accessToken,
            orgName: selectedOrganizationInfo.name
        }, {
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
            }
        });

        console.log("long-lived token")
        console.log(response.data.longLivedToken)
    };

    const onFacebookLoginFailure = (response) => {
        console.log(response)
    };

    // Add CSS styles for the organization selector container
    const orgSelectorStyles = {
        container: {
            backgroundColor: '#f7f9fc',
            padding: '14px 24px',
            marginBottom: '24px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.075)',
            border: 'solid 1px rgba(255, 255, 255, 0.075)',
            boxShadow: 'rgba(0, 0, 0, 0.02) 0px 4px 12px',
        },
        title: {
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '4px'
        },
        description: {
            fontSize: '14px',
            color: '#666',
            marginBottom: '16px'
        }
    };

    // Function to update organization name
    const updateOrganizationName = async () => {
        if (!newOrgName.trim()) {
            setNameError('Organization name cannot be empty');
            return;
        }

        try {
            const response = await axios.put(`${api}organizations/`, {
                name: newOrgName
            }, {
                headers: {
                    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                },
                params: {
                    name: selectedOrganizationInfo?.name
                }
            });

            // Update the selected organization in the app context
            setAppData(prevData => ({
                ...prevData,
                selectedOrganization: newOrgName,
                selectedOrganizationInfo: {
                    ...selectedOrganizationInfo,
                    name: newOrgName
                },
                managedOrganizations: prevData.managedOrganizations.map(org =>
                    org._id === selectedOrganizationInfo._id
                        ? { ...org, name: newOrgName }
                        : org
                )
            }));

            setIsEditingName(false);
            setNameError('');
            message.success('Organization name updated successfully');
        } catch (error) {
            console.error('Error updating organization name:', error);
            if (error.response?.data?.error === 'NAME_EXISTS') {
                setNameError('This organization name already exists. Please choose a different name.');
            } else {
                setNameError('Failed to update organization name. Please try again.');
            }
        }
    };

    return (
        <>
            <div id="fb-root"></div>
            <script async defer crossorigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v21.0&appId=532572019519772"></script>

            <div className="wrapper">
                <Navbar />
                <div className="settings-wrapper">
                    <h1 className="settings-title">Organizer Settings</h1>

                    {/* Organization Selector Section */}
                    <div className="sttngs-div">
                        <h2>Select Organization</h2>

                        <p className="org-manage-text"> Select an organization to manage </p>

                        <OrganizationSelector />
                        {/* {selectedOrganizationInfo && (
                            <div style={{ marginTop: '8px', fontSize: '14px' }}>
                                Managing settings for <strong>{selectedOrganizationInfo.name}</strong>
                            </div>
                        )} */}
                    </div>

                    {!selectedOrganizationInfo ? (
                        <Alert
                            message="No Organization Selected"
                            description="Please select an organization from the dropdown above to manage its settings."
                            type="info"
                            showIcon
                            style={{ marginBottom: '24px' }}
                        />
                    ) : (
                        <>
                            {/* Organization Name Section */}
                            <div className="sttngs-div">
                                <h2>Organization Name</h2>
                                <p className="user-descr">Edit your organization's name.</p>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    {isEditingName ? (
                                        <div style={{ width: '100%' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                                <Input
                                                    type="text"
                                                    value={newOrgName}
                                                    onChange={(e) => setNewOrgName(e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px 12px',
                                                        borderRadius: '4px',
                                                        border: nameError ? '1px solid #ff4d4f' : '1px solid #d9d9d9'
                                                    }}
                                                />
                                                <Button
                                                    type="primary"
                                                    onClick={updateOrganizationName}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    onClick={() => {
                                                        setIsEditingName(false);
                                                        setNewOrgName(selectedOrganizationInfo.name);
                                                        setNameError('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                            {nameError && (
                                                <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '4px' }}>
                                                    {nameError}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <span style={{ fontSize: '16px', fontWeight: 500 }}>
                                                {selectedOrganizationInfo.name}
                                            </span>
                                            <Button
                                                type="link"
                                                onClick={() => setIsEditingName(true)}
                                                style={{ padding: '4px' }}
                                            >
                                                Edit
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="sttngs-div">
                                <h2>Users</h2>
                                <p className="user-descr">Manage your organization's users and their roles.</p>
                                <div className="user-table">
                                    {users.map(renderUserRow)}
                                </div>

                                {/* Add User Form */}
                                <form className="add-user-form" onSubmit={handleAddUser}>
                                    <Input
                                        className='add-user-input'
                                        type="email"
                                        placeholder="Enter email address"
                                        value={newUser}
                                        onChange={(e) => {
                                            setNewUser(e.target.value);
                                            if (error) setError(false);
                                        }}
                                        required
                                    />
                                    <Dropdown
                                        menu={{ items: userTypeItems, onClick: handleMenuClick }}
                                        trigger={['click']}
                                    >
                                        <Button className="add-user-dropdown">
                                            <Space>
                                                {newUserType}
                                                <DownOutlined />
                                            </Space>
                                        </Button>
                                    </Dropdown>
                                    <Button type="primary" htmlType="submit" className="add-user-btn">
                                        Add User
                                    </Button>
                                </form>
                                {error && <p className="error-message">{error}</p>}
                            </div>

                            {/* Stripe Account Setup */}
                            <div className="sttngs-div">
                                <h2 className="payment-title">Payments</h2>


                                {selectedOrganizationInfo.stripeAccountId && selectedOrganizationInfo.stripeAccountStatus === "active" && (
                                    <>
                                        <p className="payment-subtext">
                                            Track your event's finances easily with our Stripe integration.
                                            View ticket sales, revenue, and upcoming payouts all in one place.
                                            Manage refunds and resolve payment issues quickly from your user-friendly dashboard.
                                        </p>
                                        <Button

                                            onClick={async () => {
                                                console.log("getting link")
                                                try {
                                                    const response = await axios.get(`${api}dashboard-link/${selectedOrganizationInfo.stripeAccountId}`);
                                                    window.open(response.data.url, '_blank');
                                                } catch (error) {
                                                    console.error('Error accessing dashboard:', error);
                                                    // Optionally add a simple alert
                                                    alert('Failed to access dashboard. Please try again.');
                                                }
                                            }}
                                        >
                                            View Stripe Dashboard
                                        </Button>
                                    </>
                                )}
                                {!selectedOrganizationInfo.stripeAccountId && (
                                    <div>
                                        <p className="payment-subtext"> Connect your Stripe account to start creating paid events.
                                            This allows you to securely collect payments from attendees and manage your event revenue.</p>
                                        <Button
                                            // className="stripe-btn"
                                            onClick={async () => {
                                                setAccountCreatePending(true);
                                                setError(false);
                                                try {
                                                    const response = await axios.post(`${api}stripe/account`, {
                                                        name: selectedOrganization,
                                                    }, {
                                                        headers: {
                                                            'Content-Type': 'application/json',
                                                            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                                                        }
                                                    });
                                                    const { account } = response.data;
                                                    if (account) {
                                                        setConnectedAccountId(account);
                                                        setAccountLinkCreatePending(true);
                                                        const linkResponse = await axios.post(`${api}stripe/account_link`, {
                                                            account: account,
                                                        }, {
                                                            headers: {
                                                                'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                                                                "Content-Type": "application/json",
                                                            },
                                                        });
                                                        const { url } = linkResponse.data;
                                                        if (url) {
                                                            window.location.href = url;
                                                        }
                                                    }
                                                } catch (error) {
                                                    console.error(error);
                                                    setError(true);
                                                } finally {
                                                    setAccountCreatePending(false);
                                                    setAccountLinkCreatePending(false);
                                                }
                                            }}
                                        >
                                            Connect Stripe Account
                                        </Button>
                                    </div>
                                )}
                                {selectedOrganizationInfo.stripeAccountId && selectedOrganizationInfo.stripeAccountStatus === "pending" && (
                                    <>
                                        <p className="payment-subtext">Your Stripe account is awaiting approval.
                                            To complete the process, click here to provide any required additional information.</p>
                                        <Button
                                            // className="stripe-btn"
                                            onClick={async () => {
                                                setAccountLinkCreatePending(true);
                                                setError(false);
                                                try {
                                                    const response = await axios.post(`${api}stripe/account_link`, {
                                                        account: selectedOrganizationInfo.stripeAccountId,
                                                    }, {
                                                        headers: {
                                                            'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY,
                                                            "Content-Type": "application",
                                                            "Content-Type": "application/json",
                                                        },
                                                    });
                                                    const { url } = response.data;
                                                    if (url) {
                                                        window.location.href = url;
                                                    }
                                                } catch (error) {
                                                    console.error(error);
                                                    setError(true);
                                                } finally {
                                                    setAccountLinkCreatePending(false);
                                                }
                                            }}
                                        >
                                            Add Information
                                        </Button>
                                    </>
                                )}
                                {error && <p className="error">Something went wrong!</p>}
                                {/* <div className="info-callout"> */}
                                {/* <p>
                                                This is a sample app for Stripe-hosted Connect onboarding. <a href="https://docs.stripe.com/connect/onboarding/quickstart?connect-onboarding-surface=hosted" target="_blank" rel="noopener noreferrer">View docs</a>
                                            </p> */}
                                {/* </div> */}


                            </div>

                            {/* lists */}
                            <div className="sttngs-div">
                                <h2 className="payment-title">Messaging Lists</h2>
                                <p className="payment-subtext"> Create messaging lists to send email and SMS blasts to your attendees.</p>
                                <Button
                                    onClick={() => navigate('/lists')}

                                >
                                    View My Lists
                                </Button>
                            </div>

                            {/* Social Media */}
                            <div className="sttngs-div">
                                <h2 className="payment-title">Social Media</h2>
                                {/* if facebook not linked */}
                                {!selectedOrganizationInfo.facebookAccessToken && (
                                    <>
                                        <p className="payment-subtext"> Connect your Instagram and Facebook accounts to enable-automatic cross-posting.
                                            To post to Instagram, your Instagram must be a Business account and linked to your organization's Facebook page.
                                        </p>
                                        <FacebookLogin appId={process.env.REACT_APP_FACEBOOK_APP_ID} onFacebookLoginSuccess={onFacebookLoginSuccess} onFacebookLoginFailure={onFacebookLoginFailure} />
                                    </>
                                )}

                                {/* if facebook is linked */}
                                {selectedOrganizationInfo.facebookAccessToken && (
                                    <div>
                                        <p className="payment-subtext"> Your Facebook is connected! To post to Facebook, simply create an event and select "Post to Facebook"
                                        </p>
                                    </div>
                                )}

                                {error && <p className="error">Something went wrong!</p>}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
}

export default OrganizerSettings;